import { useMemo } from "react"
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import { Loader } from "@productlane/ui"

import { api } from "@/lib/api"
import { formatWeekLabel, formatWeekRangeLabel } from "./week-label"

const formatEur = (value: number) => `€${value.toLocaleString("de-DE")}`

interface TooltipProps {
	active?: boolean
	payload?: Array<{ value: number; payload: { weekRange: string } }>
}

function MrrTooltip({ active, payload }: TooltipProps) {
	if (active && payload && payload.length > 0) {
		return (
			<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
				<p className="mb-1 text-sm font-medium text-gray-900 dark:text-white">
					{payload[0]?.payload.weekRange}
				</p>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					MRR: {formatEur(payload[0]?.value ?? 0)}
				</p>
			</div>
		)
	}
	return null
}

export function MrrChart() {
	const mrrQuery = api.analytics.mrrChart.useQuery({})

	const chartData = useMemo(() => {
		if (!mrrQuery.data) return []
		return mrrQuery.data.map((item) => ({
			...item,
			weekLabel: formatWeekLabel(item.bucket),
			weekRange: formatWeekRangeLabel(item.bucket),
		}))
	}, [mrrQuery.data])

	return (
		<div className="rounded-lg border p-4">
			<h3 className="mb-3 text-sm font-medium text-gray-800 dark:text-white">
				MRR
			</h3>
			<div className="h-64">
				{mrrQuery.isLoading ? (
					<div className="flex h-full items-center justify-center">
						<Loader />
					</div>
				) : chartData.length > 0 ? (
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={chartData}
							margin={{ left: -10, right: 0 }}
							maxBarSize={12}
						>
							<CartesianGrid
								className="stroke-gray-200 dark:stroke-gray-700"
								vertical={false}
							/>
							<XAxis
								dataKey="weekLabel"
								tick={{ fontSize: 10 }}
								angle={-45}
								textAnchor="end"
								height={50}
								tickLine={false}
								axisLine={{
									className: "stroke-gray-300 dark:stroke-gray-700",
								}}
							/>
							<YAxis
								tick={{ fontSize: 10 }}
								axisLine={false}
								tickLine={false}
								tickFormatter={(value: number) => formatEur(value)}
							/>
							<Tooltip
								content={<MrrTooltip />}
								cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
							/>
							<Bar
								dataKey="mrr"
								fill="#8b5cf6"
								radius={[4, 4, 0, 0]}
								activeBar={{ fill: "#a78bfa", opacity: 0.8 }}
							/>
						</BarChart>
					</ResponsiveContainer>
				) : (
					<div className="flex h-full items-center justify-center text-sm text-secondary">
						No MRR data yet
					</div>
				)}
			</div>
		</div>
	)
}
